import React, { useState, useEffect } from "react";
// --- NEW IMPORT: useNavigate for redirection ---
import { useNavigate } from "react-router-dom";
import { Professional, Service, Location } from "./types";

interface ConfirmationModalProps {
  professional: Professional;
  selectedService: Service | null;
  selectedLocation: Location | null;
  selectedDate: string | null;
  selectedTime: string | null;
  onConfirm: (response: any) => void;
  onClose: () => void;
}

// Helper to read cookies (Unchanged)
function getCookie(name: string) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  professional,
  selectedService,
  selectedLocation,
  selectedDate,
  selectedTime,
  onConfirm,
  onClose,
}) => {
  // --- NEW: Initialize Redirection Hook ---
  const navigate = useNavigate();

  // Form State
  const apiUrl = process.env.REACT_APP_API_URL || "/api";
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerNotes, setCustomerNotes] = useState(""); // NEW: Added notes state

  const phonePrefix = "+52";

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);

  // --- NEW: Time remaining for auto-redirect (for visual feedback) ---
  const [redirectTimer, setRedirectTimer] = useState(20);

  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Validation Logic (Unchanged)
  // Updated Validation Logic
  const validateForm = (): boolean => {
    setError(null);

    // Remove all non-letter characters (including spaces, accents, etc.)
    const lettersOnly = customerName.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ]/g, "");

    // Check if there are at least 3 letters in total
    if (lettersOnly.length < 3) {
      setError("El nombre debe contener al menos 3 letras.");
      return false;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(customerPhone)) {
      setError("El teléfono debe ser de 10 números (sin espacios ni guiones).");
      return false;
    }

    // Optional: Add validation for notes length if needed
    if (customerNotes.length > 500) {
      setError("Las notas no pueden exceder los 500 caracteres.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    const csrftoken = getCookie("csrftoken") || "";

    const payload = {
      professional_id: professional.id,
      service_id: selectedService?.id,
      location_id: selectedLocation?.id,
      date: selectedDate,
      time: selectedTime,
      client_timezone: clientTimezone,
      customer: {
        name: customerName.trim(),
        phone: `${phonePrefix} ${customerPhone.trim()}`,
        notes: customerNotes.trim(), // NEW: Added notes to payload
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/professional/appt_creation/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrftoken && { "X-CSRFToken": csrftoken }),
          },
          body: JSON.stringify(payload),
          credentials: "include",
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Error al agendar la cita.");
      }

      setSuccessData(data);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || "Ocurrió un error inesperado.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- UPDATED: Central function for confirmation and redirection ---
  const handleFinalize = () => {
    // 1. Notify parent component
    if (successData) {
      onConfirm(successData);
    }
    // 2. Close modal
    onClose();
    // 3. Redirect user to the root path (/)
    navigate("/");
  };

  // --- NEW: Timer logic using useEffect ---
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    let countdown: NodeJS.Timeout | undefined;

    if (isSuccess) {
      // Start the countdown displayed to the user
      countdown = setInterval(() => {
        setRedirectTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      // Set the auto-redirection timeout (20 seconds)
      timer = setTimeout(() => {
        handleFinalize();
      }, 20000); // 20 seconds
    }

    // Cleanup function: Clear timers when component unmounts or success state changes
    return () => {
      if (timer) clearTimeout(timer);
      if (countdown) clearInterval(countdown);
    };
  }, [isSuccess]); // Rerun only when isSuccess changes

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-confirmation">
        {/* === CONDITIONAL RENDERING: SUCCESS VIEW OR FORM VIEW === */}
        {isSuccess ? (
          // --- SUCCESS VIEW ---
          <div className="success-view-container">
            <div className="success-icon-wrapper">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="success-icon"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 0C5.37 0 0 5.37 0 12S5.37 24 12 24 24 18.63 24 12 18.63 0 12 0ZM10 17.2L5.4 12.6L7.24 10.76L10 13.52L16.76 6.76L18.6 8.6L10 17.2Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <h3>¡Cita Agendada!</h3>
            <p>
              Tu cita ha sido creada exitosamente.
              <br />
              Nos pondremos en contacto contigo pronto para confirmar los
              detalles.
            </p>
            <button
              className="btn btn--primary btn--large"
              onClick={handleFinalize} // Now redirects to '/'
              style={{ minWidth: "200px", justifyContent: "center" }}
            >
              Entendido
            </button>
            <small
              style={{
                marginTop: "var(--space-sm)",
                color: "var(--neutral-300)",
              }}
            >
              Serás redirigido en {redirectTimer} segundos.
            </small>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2 className="modal-title">Confirmar Cita</h2>
              <button
                className="modal-close"
                onClick={onClose}
                disabled={isLoading}
              >
                ×
              </button>
            </div>

            <div className="modal-body-layout">
              {/* Left Column: Summary (Unchanged) */}
              <div className="summary-section">
                <h3 style={{ marginBottom: "var(--space-md)", marginTop: 0 }}>
                  Resumen
                </h3>
                <p>
                  <strong>Profesional:</strong> {professional.name}
                </p>
                <p>
                  <strong>Servicio:</strong> {selectedService?.name}
                </p>
                <p>
                  <strong>Ubicación:</strong> {selectedLocation?.name}
                </p>
                <p>
                  <strong>Fecha:</strong>{" "}
                  {selectedDate ? formatDate(selectedDate) : ""}
                </p>
                <p>
                  <strong>Hora:</strong> {selectedTime}
                </p>
                <p>
                  <strong>Duración:</strong> {selectedService?.duration} minutos
                </p>
                <p
                  style={{
                    fontSize: "1.2rem",
                    color: "var(--color-secondary)",
                    marginTop: "1rem",
                  }}
                >
                  <strong>Total: ${selectedService?.price.toFixed(2)}</strong>
                </p>
              </div>

              {/* Right Column: Form (Updated with notes field) */}
              <form onSubmit={handleSubmit}>
                {/* ... error message and form inputs ... */}
                {error && (
                  <div
                    style={{
                      color: "var(--error)",
                      backgroundColor: "#ffe6e6",
                      padding: "10px",
                      borderRadius: "var(--border-radius)",
                      marginBottom: "1rem",
                      fontSize: "0.9rem",
                      border: "1px solid var(--error)",
                    }}
                  >
                    {error}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="customerName" className="form-label">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    id="customerName"
                    className="form-input"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    disabled={isLoading}
                    placeholder="Ej. Juan Pérez"
                  />
                </div>

                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label htmlFor="customerPhone" className="form-label">
                    Teléfono
                  </label>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <input
                      type="text"
                      className="form-input"
                      value={phonePrefix}
                      readOnly
                      disabled={isLoading}
                      style={{
                        width: "80px",
                        textAlign: "center",
                        backgroundColor: "var(--neutral-100)",
                        cursor: "default",
                        color: "var(--text-dark)",
                      }}
                    />
                    <input
                      type="tel"
                      id="customerPhone"
                      className="form-input"
                      value={customerPhone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        if (val.length <= 10) setCustomerPhone(val);
                      }}
                      disabled={isLoading}
                      placeholder="10 dígitos"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                {/* NEW: Notes Field */}
                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label htmlFor="customerNotes" className="form-label">
                    Notas Adicionales (Opcional)
                  </label>
                  <textarea
                    id="customerNotes"
                    className="form-input"
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    disabled={isLoading}
                    placeholder="Ej. Información adicional sobre el servicio, preferencias especiales, alergias, etc."
                    rows={4}
                    maxLength={500}
                    style={{
                      resize: "vertical",
                      minHeight: "80px",
                      fontFamily: "var(--font-body)",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color:
                        customerNotes.length >= 450
                          ? "var(--warning)"
                          : "var(--neutral-300)",
                      textAlign: "right",
                      marginTop: "4px",
                    }}
                  >
                    {customerNotes.length}/500 caracteres
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "var(--space-md)",
                    marginTop: "var(--space-xl)",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={onClose}
                    disabled={isLoading}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={isLoading}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    {isLoading ? (
                      <>
                        <span className="loader-spinner"></span>
                        Procesando...
                      </>
                    ) : (
                      "Confirmar"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>

      <style>{`
        /* ... Existing Styles (omitted for brevity) ... */
        .loader-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #ffffff;
          border-bottom-color: transparent;
          border-radius: 50%;
          display: inline-block;
          box-sizing: border-box;
          animation: rotation 1s linear infinite;
        }
        @keyframes rotation {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .success-view-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: var(--space-lg);
            animation: fadeIn 0.4s ease-out;
        }

        .success-icon-wrapper {
            width: 80px;
            height: 80px;
            margin-bottom: var(--space-md);
            color: var(--success);
        }

        .success-icon {
            width: 100%;
            height: 100%;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .form-input {
            width: 100%;
            padding: 0.75rem 1rem;
            border: 1px solid var(--neutral-300);
            border-radius: var(--border-radius);
            font-family: var(--font-body);
            font-size: 1rem;
            transition: border-color 0.2s;
        }
        .form-input:focus {
            outline: none;
            border-color: var(--color-accent);
            box-shadow: 0 0 0 3px var(--color-primary);
        }
        .form-label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 600;
            color: var(--color-secondary);
        }
      `}</style>
    </div>
  );
};

export default ConfirmationModal;
